import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, LogOut, RefreshCw } from 'lucide-react';
import { useProAuth } from '@/lib/auth-context';
import { AuthLayout } from '@/components/layout/AuthLayout';

export function PendingApprovalPage() {
  const { t } = useTranslation();
  const { user, proCustomer, isLoading, isProCustomerLoading, logout, refreshProCustomer } =
    useProAuth();

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      void refreshProCustomer();
    }, 10_000);
    return () => clearInterval(interval);
  }, [user, refreshProCustomer]);

  // Ne rien décider tant que le profil pro n'est pas résolu : afficher
  // « Approbation en attente » à un compte déjà validé, même une fraction de
  // seconde, est le clignotement qu'on corrige ici.
  if (isLoading || isProCustomerLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (proCustomer?.status === 'approved') return <Navigate to="/tableau-de-bord" replace />;

  if (proCustomer?.status === 'rejected') {
    return (
      <AuthLayout>
        <div className="text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
            <span className="text-2xl">✕</span>
          </div>
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">
            {t('pro.auth.rejected_title', 'Inscription refusée')}
          </h2>
          <p className="text-sm text-neutral-500 mb-4">
            {proCustomer.rejectedReason ||
              t('pro.auth.rejected_subtitle', 'Votre demande n’a pas été acceptée.')}
          </p>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900"
          >
            <LogOut className="w-4 h-4" />
            {t('pro.header.logout', 'Déconnexion')}
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-orange-100 flex items-center justify-center mb-4">
          <Clock className="w-7 h-7 text-orange-600" />
        </div>
        <h2 className="text-xl font-semibold text-neutral-900 mb-2">
          {t('pro.auth.pending_title', 'Approbation en attente')}
        </h2>
        <p className="text-sm text-neutral-500 mb-6">
          {t(
            'pro.auth.pending_subtitle',
            'Votre compte est en cours de validation. Vous pourrez accéder à votre espace dès que votre fournisseur aura validé votre inscription.',
          )}
        </p>
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => refreshProCustomer()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-200 text-sm font-medium hover:bg-neutral-50"
          >
            <RefreshCw className="w-4 h-4" />
            {t('pro.auth.refresh_status', 'Actualiser')}
          </button>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-neutral-600 hover:bg-neutral-50"
          >
            <LogOut className="w-4 h-4" />
            {t('pro.header.logout', 'Déconnexion')}
          </button>
        </div>
      </div>
    </AuthLayout>
  );
}
